import React from "react";

// Reusable KPI card for CRM/Teacher dashboards.
// Usage: <StatCard label="Active" value={12} color="var(--ember)" icon={Users} testid="stat-active" />
export function StatCard({ label, value, color = "var(--ink)", icon: Icon, testid, hint, onClick }) {
  const clickable = typeof onClick === "function";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      data-testid={testid}
      className="text-left w-full card-surface p-4 lg:p-5 flex items-center gap-4 disabled:opacity-100 disabled:cursor-default"
      style={{ cursor: clickable ? "pointer" : "default" }}
    >
      {Icon && (
        <div
          className="shrink-0 flex items-center justify-center rounded-md"
          style={{ width: 44, height: 44, background: "var(--surface-hover)", color }}
        >
          <Icon size={20} strokeWidth={1.75} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-[0.6rem] uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>
          {label}
        </div>
        <div className="font-serif-display text-3xl leading-none mt-1" style={{ color }}>
          {value}
        </div>
        {hint && (
          <div className="text-[0.7rem] mt-1 truncate" style={{ color: "var(--muted)" }}>
            {hint}
          </div>
        )}
      </div>
    </button>
  );
}

// Reusable empty state block. Shows an icon, headline, description, and optional CTA.
export function EmptyState({ icon: Icon, title, description, action, testid }) {
  return (
    <div
      className="card-surface p-14 lg:p-20 text-center flex flex-col items-center"
      data-testid={testid}
    >
      {Icon && (
        <div
          className="mb-5 flex items-center justify-center rounded-full"
          style={{ width: 72, height: 72, background: "var(--surface-hover)", color: "var(--gold)" }}
        >
          <Icon size={30} strokeWidth={1.5} />
        </div>
      )}
      {title && (
        <h3 className="font-serif-display text-xl lg:text-2xl" style={{ color: "var(--ink)" }}>
          {title}
        </h3>
      )}
      {description && (
        <p className="mt-2 max-w-md text-sm" style={{ color: "var(--muted)" }}>
          {description}
        </p>
      )}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

// Compact circular progress ring (SVG). Great for guest overall completion.
export function ProgressRing({ value, total, size = 42, stroke = 4, color = "var(--ember)", label }) {
  const pct = total > 0 ? Math.min(1, value / total) : 0;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - pct);
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }} aria-label={label}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--border-subtle)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.35s ease" }}
        />
      </svg>
      <span
        className="absolute font-serif-display leading-none"
        style={{ color: "var(--ink)", fontSize: size < 40 ? "0.65rem" : "0.75rem" }}
      >
        {total > 0 ? Math.round(pct * 100) + "%" : "—"}
      </span>
    </div>
  );
}
